<section class="documentation-hero">
    <div class="container">
        <div class="row align-items-center">
            <div class="col-lg-6">
                <div class="hero-content">
                    <span class="hero-badge">Comprehensive Guides</span>
                    <h1 class="hero-title">Documentation & Resources</h1>
                    <p class="hero-description">Explore our comprehensive documentation, tutorials, and resources to get the most out of our cloud infrastructure services. Find everything you need to deploy, manage, and optimize your solutions.</p>
                    
                    <div class="hero-actions">
                        <a class="btn-primary-gradient me-3" routerLink="/contact">
                            <i class="bx bx-phone"></i>
                            Contact Support
                        </a>
                        <a class="btn-outline-light" routerLink="/pricing">
                            <i class="bx bx-credit-card"></i>
                            View Pricing
                        </a>
                    </div>
                    
                    <div class="hero-features">
                        <div class="feature-item">
                            <i class="bx bx-check-circle"></i>
                            <span>Step-by-Step Tutorials</span>
                        </div>
                        <div class="feature-item">
                            <i class="bx bx-check-circle"></i>
                            <span>API Documentation</span>
                        </div>
                        <div class="feature-item">
                            <i class="bx bx-check-circle"></i>
                            <span>Best Practices</span>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-lg-6">
                <div class="hero-image">
                    <div class="image-decoration">
                        <div class="floating-card card-1">
                            <i class="bx bx-book"></i>
                            <span>Guides & Tutorials</span>
                        </div>
                        <div class="floating-card card-2">
                            <i class="bx bx-code"></i>
                            <span>API Reference</span>
                        </div>
                        <div class="floating-card card-3">
                            <i class="bx bx-help-circle"></i>
                            <span>FAQ & Support</span>
                        </div>
                    </div>
                    <img src="assets/img/documentation-hero.png" alt="Documentation" class="img-fluid main-image">
                </div>
            </div>
        </div>
    </div>
    <div class="hero-background-elements">
        <div class="bg-element element-1"></div>
        <div class="bg-element element-2"></div>
        <div class="bg-element element-3"></div>
    </div>
</section>

<section class="documentation-section ptb-100">
    <div class="container">
        <div class="section-title">
            <span class="section-badge">Knowledge Base</span>
            <h2>Documentation Categories</h2>
            <p>Explore our comprehensive documentation organized by category to find exactly what you need.</p>
        </div>

        <div class="documentation-categories">
            <div class="category-card">
                <div class="category-header">
                    <div class="category-icon">
                        <i class="bx bx-rocket"></i>
                    </div>
                </div>
                <h3>Getting Started</h3>
                <p>Begin your journey with our platform. Learn how to create an account, set up your first instance, and configure basic settings.</p>
                <a class="category-link" routerLink="/documentation/getting-started">
                    Explore Guides <i class="bx bx-chevron-right"></i>
                </a>
            </div>

            <div class="category-card">
                <div class="category-header">
                    <div class="category-icon">
                        <i class="bx bx-code"></i>
                    </div>
                </div>
                <h3>API Documentation</h3>
                <p>Comprehensive reference for our RESTful APIs. Includes endpoints, parameters, authentication, and code examples.</p>
                <a class="category-link" routerLink="/documentation/api">
                    View API Docs <i class="bx bx-chevron-right"></i>
                </a>
            </div>

            <div class="category-card">
                <div class="category-header">
                    <div class="category-icon">
                        <i class="bx bx-server"></i>
                    </div>
                </div>
                <h3>Cloud Services</h3>
                <p>Detailed guides for all our cloud services including Windows Cloud, Linux Cloud, GPU Computing, and Storage Solutions.</p>
                <a class="category-link" routerLink="/documentation/cloud-services">
                    Learn More <i class="bx bx-chevron-right"></i>
                </a>
            </div>

            <div class="category-card">
                <div class="category-header">
                    <div class="category-icon">
                        <i class="bx bx-shield"></i>
                    </div>
                </div>
                <h3>Security & Compliance</h3>
                <p>Learn about our security measures, compliance standards, and best practices for securing your infrastructure.</p>
                <a class="category-link" routerLink="/documentation/security">
                    Security Guide <i class="bx bx-chevron-right"></i>
                </a>
            </div>

            <div class="category-card">
                <div class="category-header">
                    <div class="category-icon">
                        <i class="bx bx-cog"></i>
                    </div>
                </div>
                <h3>Management & Monitoring</h3>
                <p>Tools and techniques for managing your instances, monitoring performance, and optimizing resources.</p>
                <a class="category-link" routerLink="/documentation/management">
                    Management Docs <i class="bx bx-chevron-right"></i>
                </a>
            </div>

            <div class="category-card">
                <div class="category-header">
                    <div class="category-icon">
                        <i class="bx bx-trending-up"></i>
                    </div>
                </div>
                <h3>Performance Optimization</h3>
                <p>Advanced techniques for optimizing your cloud infrastructure for better performance and cost efficiency.</p>
                <a class="category-link" routerLink="/documentation/optimization">
                    Optimization Tips <i class="bx bx-chevron-right"></i>
                </a>
            </div>
        </div>
    </div>
</section>

<section class="search-section">
    <div class="container">
        <div class="search-content">
            <h2>Can't Find What You're Looking For?</h2>
            <p>Search our entire documentation knowledge base for specific topics, guides, or solutions.</p>
            <form class="search-form">
                <input type="text" class="search-input" placeholder="Search documentation...">
                <button type="submit" class="search-button">
                    <i class="bx bx-search"></i> Search
                </button>
            </form>
        </div>
    </div>
</section>

<section class="documentation-section ptb-100">
    <div class="container">
        <div class="popular-articles">
            <div class="section-title text-center">
                <span class="section-badge">Popular Articles</span>
                <h2>Most Viewed Documentation</h2>
                <p>Check out our most frequently accessed guides and tutorials</p>
            </div>

            <div class="articles-grid">
                <div class="article-card">
                    <h4>Setting Up Your First Windows Cloud Instance</h4>
                    <p>Step-by-step guide to deploying and configuring your first Windows Server instance in the cloud.</p>
                    <div class="article-meta">
                        <div class="article-tags">
                            <span class="tag">Windows Cloud</span>
                            <span class="tag">Getting Started</span>
                        </div>
                        <span>8 min read</span>
                    </div>
                </div>

                <div class="article-card">
                    <h4>GPU Cloud Computing: Performance Tuning Guide</h4>
                    <p>Advanced optimization techniques for maximizing GPU performance in machine learning and compute workloads.</p>
                    <div class="article-meta">
                        <div class="article-tags">
                            <span class="tag">GPU Cloud</span>
                            <span class="tag">Performance</span>
                        </div>
                        <span>12 min read</span>
                    </div>
                </div>

                <div class="article-card">
                    <h4>Security Best Practices for Cloud Infrastructure</h4>
                    <p>Essential security measures and configurations to protect your cloud resources and data.</p>
                    <div class="article-meta">
                        <div class="article-tags">
                            <span class="tag">Security</span>
                            <span class="tag">Compliance</span>
                        </div>
                        <span>10 min read</span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</section>

<section class="documentation-contact-section ptb-100">
    <div class="container">
        <div class="row align-items-center">
            <div class="col-lg-6">
                <div class="contact-content">
                    <div class="section-title">
                        <span class="section-badge">Need More Help?</span>
                        <h2>Can't Find Your Answer?</h2>
                        <p>Our documentation team is ready to assist you with any questions or specific documentation requests you may have.</p>
                    </div>
                    
                    <div class="contact-features">
                        <div class="contact-feature-item">
                            <i class="bx bx-phone"></i>
                            <div class="contact-feature-content">
                                <h5>Documentation Support</h5>
                                <p>Get help with our documentation</p>
                            </div>
                        </div>
                        <div class="contact-feature-item">
                            <i class="bx bx-chat"></i>
                            <div class="contact-feature-content">
                                <h5>Live Chat</h5>
                                <p>Instant assistance available 24/7</p>
                            </div>
                        </div>
                        <div class="contact-feature-item">
                            <i class="bx bx-envelope"></i>
                            <div class="contact-feature-content">
                                <h5>Email Support</h5>
                                <p>docs@insightfulitservices.com</p>
                            </div>
                        </div>
                    </div>
                    
                    <div class="contact-actions">
                        <a class="btn-primary-gradient me-3" routerLink="/contact">
                            <i class="bx bx-phone"></i>
                            Contact Documentation Team
                        </a>
                        <a class="btn-outline-light" routerLink="/sign-up">
                            <i class="bx bx-user-plus"></i>
                            Start Free Trial
                        </a>
                    </div>
                </div>
            </div>
            <div class="col-lg-6">
                <div class="contact-image">
                    <img src="assets/img/documentation-contact.png" alt="Documentation Support" class="img-fluid">
                    <div class="floating-elements">
                        <div class="floating-badge badge-1">
                            <i class="bx bx-book"></i>
                            <span>Comprehensive Guides</span>
                        </div>
                        <div class="floating-badge badge-2">
                            <i class="bx bx-time"></i>
                            <span>&lt; 1 Hour Response</span>
                        </div>
                        <div class="floating-badge badge-3">
                            <i class="bx bx-check-circle"></i>
                            <span>Expert Assistance</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <div class="contact-background-elements">
        <div class="bg-element element-1"></div>
        <div class="bg-element element-2"></div>
    </div>
</section>